import React, { useMemo, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";

import { DataGrid } from "@toddle-edu/ds-data-grid";
import { EmptyState, Dialog, Button } from "@toddle-edu/ds-web";
import { NoClassesIllustration } from "@toddle-edu/ds-theme";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";

import { getSettingValue } from "Utils/ConfigurationUtils";
import { updateSettings } from "modules/ConfigurationThunks";

import {
  getSelectedAcademicYear,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  getMomentFromDateAndTimeString,
  getOpensOnBasedOnTimelineSettings,
  getDueOnBasedOnTimelineSettings,
} from "../../Utils";
import {
  formatDate,
  checkIsDateAfter,
  checkIsDateBefore,
} from "Utils/DateTimeHelpers";
import { useI18n } from "Hooks";
import {
  transformCoursesToGridData,
  createDataGridHeaders,
  getCourseStudentsFromUserMap,
} from "./DataGridHelpers";
import AssignClassSettingsDataGridFooter from "../AssignClassSettingsDataGridFooter";
import WorksheetAssignSummary from "../WorksheetAssignSummary";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getIsWorksheetAssignModalV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

import {
  VISIBLE,
  TO_BE_VISIBLE,
  PRIVATE,
  OPENS_ON,
  DUE_ON,
  CLOSES_ON,
  VISIBILITY,
  STUDENTS,
  TAGS,
  SECURITY_MODE,
  DURATION,
} from "Constants/stringConstants";
import {
  UNRESTRICTED,
  BROWSER_LOCK,
  LOCKDOWN_BROWSER,
} from "Worksheet/modules/WorksheetConstants";

import classes from "./AssignClassSettingsDataGrid.scss";

const AssignClassSettingsDataGrid = ({
  courses,
  mode,
  updateSelectedItems,
  academicYearSelected,
  genericFolders,
  coursesUserMap,
  visibilityOptionPreference,
  isFixedStartTime,
  allowBackDate,
  selectedTimeSlot,
  isDraftMode,
  currentTimelineSettings,
  dueTimePreference,
  assignmentData,
  isSubmissionPendingForAll,
  isStudentSubmissionEnabled,
  filteredStaffCoursesIds,
  updateSettings,
  courseId,
  isDuplicateAssignment,
  isIncludedInCourseFlow,
  state: assignmentsState,
  classWiseSettingsToDisplay,
  assessmentTitle,
  handleAddClassClick,
  timedTestDuration,
  updateSelectedClassItems,
  assignmentDefaultSettings = {},
  isLockScreenEnabled,
  isSafeBrowserEnabled,
  isTimedTestEnabled,
  isWorksheet,
}) => {
  const { t } = useI18n();

  // Derive security mode from boolean flags
  const securityMode = isSafeBrowserEnabled
    ? LOCKDOWN_BROWSER
    : isLockScreenEnabled
    ? BROWSER_LOCK
    : UNRESTRICTED;

  const isWorksheetLayoutUpdateEnabled =
    isWorksheet && getIsWorksheetAssignModalV2Enabled();

  // states
  const [courseSelectedFoldersMap, setCourseSelectedFoldersMap] = useState({});
  const [removeClassDialogParams, setRemoveClassDialogParams] = useState(null);

  // variables
  const { course: { title: removedClassLabel } = {} } =
    removeClassDialogParams || {};
  const oldCourses = _.get(assignmentData, "courses", []);
  const {
    opensOn: opensOnPreference,
    dueOn: dueOnPreference,
    closesOn: closesOnPreference,
  } = assignmentDefaultSettings || {};

  // side effects

  // In edit assignment for all initial courses folderNames will be list of folderIds
  // Set folders which are part of the current course
  useEffect(() => {
    if (mode === "edit") {
      const coursesWithFolders = _.filter(courses, course => {
        return !_.isEmpty(course?.folderNames);
      });
      if (!_.isEmpty(coursesWithFolders)) {
        const newCourseSelectedFoldersMap = _.reduce(
          coursesWithFolders,
          (newCourseSelectedFoldersMap, { id, folderNames = [] } = {}) => {
            newCourseSelectedFoldersMap[id] = _.filter(
              genericFolders,
              ({ value }) => _.includes(folderNames, value)
            );
            return newCourseSelectedFoldersMap;
          },
          {}
        );
        setCourseSelectedFoldersMap(prev => ({
          ...prev,
          ...newCourseSelectedFoldersMap,
        }));
      }
    }
  }, []);

  // For all default courses selected, remove students...
  // which are not part of coursesUserMap(data from query) in case of create and draft
  useEffect(() => {
    if (
      (assignmentsState === "DRAFT" || mode === "create") &&
      !_.isEmpty(coursesUserMap)
    ) {
      const coursesToUpdate = _.reduce(
        courses,
        (result, course) => {
          const { id, students = [] } = course;
          const courseStudents = getCourseStudentsFromUserMap({
            coursesUserMap,
            courseId: id,
          });
          const courseStudentIds = _.map(courseStudents, "id");
          const filteredStudents = _.filter(students, student =>
            _.includes(courseStudentIds, student.id)
          );
          if (!_.isEqual(students, filteredStudents)) {
            result.push({
              ...course,
              students: filteredStudents,
            });
          } else {
            result.push(course);
          }
          return result;
        },
        []
      );

      if (!_.isEmpty(coursesToUpdate)) {
        updateSelectedItems({
          courses: coursesToUpdate,
        });
      }
    }
  }, [coursesUserMap]);

  // When user deselects "Opens on" from configure settings...
  // then set visibility to VISIBLE for all courses having visibility as TO_BE_VISIBLE
  useEffect(() => {
    const coursesToUpdate = _.reduce(
      courses,
      (result, course) => {
        const {
          id,
          state = {},
          visibility,
          deadlineDate,
          deadlineTime,
        } = course;
        const oldCourseData = _.find(oldCourses, { id }) || {};
        const {
          state: {
            scheduleDate: oldScheduleDate,
            scheduleTime: oldScheduleTime,
          } = {},
        } = oldCourseData || {};

        const { scheduleDate, scheduleTime } = state;

        const opensOn = getMomentFromDateAndTimeString({
          dateString: scheduleDate,
          timeString: scheduleTime,
        });

        const previousOpensOn = getMomentFromDateAndTimeString({
          dateString: oldScheduleDate,
          timeString: oldScheduleTime,
        });

        const dueOn = getMomentFromDateAndTimeString({
          dateString: deadlineDate,
          timeString: deadlineTime,
        });
        const dueOnToConsider = getDueOnBasedOnTimelineSettings({
          dueOn,
          currentTimelineSettings,
        });
        const isDueDateInPast = checkIsDateBefore({
          date1: dueOnToConsider,
          date2: moment(),
          key: "minutes",
        });
        const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
          opensOn,
          previousOpensOn,
          currentTimelineSettings,
          isDueDateInPast,
        });

        if (_.isEmpty(opensOnToConsider) && visibility === TO_BE_VISIBLE) {
          result.push({
            ...course,
            visibility: VISIBLE,
          });
        }
        return result;
      },
      []
    );

    if (!_.isEmpty(coursesToUpdate)) {
      updateSelectedItems({
        courses: coursesToUpdate,
      });
    }
  }, [currentTimelineSettings]);

  // Create update function for specific course
  const createUpdateFunction = useCallback(
    courseId => {
      return params => {
        updateSelectedClassItems({ ...params, id: courseId });
      };
    },
    [updateSelectedClassItems]
  );

  useEffect(() => {
    if (
      visibilityOptionPreference !== PRIVATE ||
      _.isEmpty(courses) ||
      mode === "edit"
    ) {
      return;
    }

    const coursesToUpdate = _.reduce(
      courses,
      (result, course) => {
        if (course?.visibility !== PRIVATE) {
          result.push({
            ...course,
            visibility: PRIVATE,
          });
        }
        return result;
      },
      []
    );

    if (!_.isEmpty(coursesToUpdate)) {
      updateSelectedItems({ courses: coursesToUpdate });
    }
  }, []);

  // handlers
  const handleStudentChange = useCallback(
    ({ updateItem, value, allStudents }) => {
      const updatedStudents = _.filter(allStudents, ({ id }) => {
        return _.find(value, { id });
      });

      updateItem({
        students: updatedStudents,
      });
    },
    []
  );

  const handleOpenDateChange = useCallback(
    ({ course, updateItem, value }) => {
      const { state, visibility } = course;

      if (_.isEmpty(value)) {
        updateItem({
          state: {
            ...state,
            scheduleDate: null,
            scheduleTime: null,
          },
          visibility: visibility === TO_BE_VISIBLE ? VISIBLE : visibility,
        });
        return;
      }

      const updatedOpenDate = formatDate({
        date: value,
        format: "YYYY-MM-DD",
      });
      const updatedOpenTime = formatDate({
        date: value,
        format: "HH:mm",
      });
      const updatedOpensOn = getMomentFromDateAndTimeString({
        dateString: updatedOpenDate,
        timeString: updatedOpenTime,
      });

      const isOpenDateInFuture = checkIsDateAfter({
        date1: updatedOpensOn,
        date2: moment(),
        key: "minutes",
      });

      // If selected open date in future and user has set visibility as TO_BE_VISIBLE previously, then set visibility as TO_BE_VISIBLE
      let visibilityValue = visibility;
      if (isOpenDateInFuture && visibilityOptionPreference === TO_BE_VISIBLE) {
        visibilityValue = visibilityOptionPreference;
      } else if (visibility === TO_BE_VISIBLE && !isOpenDateInFuture) {
        visibilityValue = VISIBLE;
      }

      updateItem({
        state: {
          ...state,
          scheduleDate: updatedOpenDate,
          scheduleTime: updatedOpenTime,
        },
        visibility: visibilityValue,
      });
    },
    [visibilityOptionPreference]
  );

  const handleDueDateChange = useCallback(
    ({ course, updateItem, value }) => {
      if (_.isEmpty(value)) {
        updateItem({ deadlineDate: null, deadlineTime: null });
        return;
      }

      const updatedDueDate = formatDate({
        date: value,
        format: "YYYY-MM-DD",
      });
      const updatedDueTime = formatDate({
        date: value,
        format: "HH:mm",
      });

      updateItem({
        deadlineDate: updatedDueDate,
        deadlineTime: updatedDueTime,
      });

      const { id, state = {} } = course;
      const oldCourseData = _.find(oldCourses, { id }) || {};
      const {
        state: {
          scheduleDate: oldScheduleDate,
          scheduleTime: oldScheduleTime,
        } = {},
      } = oldCourseData || {};
      const { scheduleDate, scheduleTime } = state;

      const opensOn = getMomentFromDateAndTimeString({
        dateString: scheduleDate,
        timeString: scheduleTime,
      });
      const previousOpensOn = getMomentFromDateAndTimeString({
        dateString: oldScheduleDate,
        timeString: oldScheduleTime,
      });

      // If due date set in past and opens on is empty, prefill opens on date as 1 min before due date
      const isDueDateInPast = checkIsDateBefore({
        date1: value,
        date2: moment(),
        key: "minutes",
      });
      const opensOnToConsider = getOpensOnBasedOnTimelineSettings({
        opensOn,
        previousOpensOn,
        currentTimelineSettings,
      });

      if (isDueDateInPast && _.isEmpty(opensOnToConsider)) {
        const updatedOpensOn = moment(value).subtract(1, "minute");
        updateItem({
          deadlineDate: updatedDueDate,
          deadlineTime: updatedDueTime,
          state: {
            ...state,
            scheduleDate: formatDate({
              date: updatedOpensOn,
              format: "YYYY-MM-DD",
            }),
            scheduleTime: formatDate({
              date: updatedOpensOn,
              format: "HH:mm",
            }),
          },
        });
      }
    },
    [currentTimelineSettings, oldCourses]
  );

  const handleCloseDateChange = useCallback(({ updateItem, value }) => {
    if (_.isEmpty(value)) {
      updateItem({ closeSubmissionDate: null });
      return;
    }

    updateItem({
      closeSubmissionDate: formatDate({
        date: value,
        format: "YYYY-MM-DD:HH:mm",
      }),
    });
  }, []);

  const handleVisibilityChange = useCallback(
    ({ updateItem, value, course }) => {
      const newValue = _.first(value)?.value;

      // If visibility is set to TO_BE_VISIBLE, do not update if opensOn is not in future
      // This case comes when copy visibility value from one cell and paste to another where opensOn is not in future
      const { state = {} } = course || {};
      const { scheduleDate, scheduleTime } = state;
      const opensOn = getMomentFromDateAndTimeString({
        dateString: scheduleDate,
        timeString: scheduleTime,
      });
      const isOpenDateInFuture =
        !_.isEmpty(opensOn) &&
        checkIsDateAfter({
          date1: opensOn,
          date2: moment(),
          key: "minutes",
        });
      if (!isOpenDateInFuture && newValue === TO_BE_VISIBLE) {
        return;
      }

      const updatedValue = { visibility: newValue };
      updateItem(updatedValue);

      // save visibility option preference
      try {
        updateSettings({
          settings: [
            {
              name: "AssignmentVisibilityOptionPreference",
              value: JSON.stringify(updatedValue),
            },
          ],
          showToastMsg: false,
        });
      } catch (err) {}
    },
    [updateSettings]
  );

  const handleTagChange = useCallback(
    ({ course, updateItem, value: newTags }) => {
      setCourseSelectedFoldersMap(prev => ({
        ...prev,
        [course?.id]: newTags,
      }));
      switch (mode) {
        case "create":
          updateItem({
            folderNames: _.compact(_.map(newTags, tag => tag?.label)),
          });
          break;
        case "edit": {
          const oldCourseData = _.find(oldCourses, { id: course?.id }) || {};
          const isExistingClass = mode === "edit" && !_.isEmpty(oldCourseData);

          const folderNames = isExistingClass
            ? _.compact(
                _.map(newTags, folder => folder?.value || folder?.label)
              )
            : _.compact(_.map(newTags, folder => folder?.label));

          updateItem({ folderNames });
          break;
        }
        default:
          updateItem({ selectedFolders: newTags });
      }
    },
    [mode, oldCourses]
  );

  const handleSecurityModeChange = useCallback(
    ({ updateItem, value }) => {
      const selectedValue = _.first(value)?.value;
      const securityUpdate = {
        isLockScreenEnabled: selectedValue === BROWSER_LOCK,
        isSafeBrowserEnabled: selectedValue === LOCKDOWN_BROWSER,
      };
      if (isWorksheetLayoutUpdateEnabled) {
        updateItem(securityUpdate);
      } else {
        updateSelectedItems(securityUpdate);
      }
    },
    [updateSelectedItems, isWorksheetLayoutUpdateEnabled]
  );

  const handleDurationChange = useCallback(
    ({ updateItem, value }) => {
      const totalSeconds = value;
      if (!_.isNil(totalSeconds)) {
        const durationUpdate = {
          timedTestDuration: {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
          },
        };
        if (isWorksheetLayoutUpdateEnabled) {
          updateItem(durationUpdate);
        } else {
          updateSelectedItems(durationUpdate);
        }
      }
    },
    [updateSelectedItems, isWorksheetLayoutUpdateEnabled]
  );

  const handleRemoveClassConfirm = useCallback(
    courseId => {
      const updatedCourses = _.filter(
        courses,
        course => course?.id !== courseId
      );
      updateSelectedItems({ courses: updatedCourses });
      setRemoveClassDialogParams(null);
    },
    [courses, updateSelectedItems]
  );

  const handleDialogClose = useCallback(() => {
    setRemoveClassDialogParams(null);
  }, []);

  const handleRemoveClass = useCallback(
    ({ rowId: courseId }) => {
      if (
        mode === "edit" &&
        assignmentsState !== "DRAFT" &&
        getIsLearningCoursesEnabled()
      ) {
        const course = _.find(courses, { id: courseId }) || {};
        setRemoveClassDialogParams({ course });
        return;
      }
      handleRemoveClassConfirm(courseId);
    },
    [courses, mode, assignmentsState, handleRemoveClassConfirm]
  );

  // Handle cell edits
  const handleCellEdit = useCallback(
    cells => {
      _.forEach(cells, cell => {
        const { cellCoods, newValue } = cell;
        const { colId, rowId } = cellCoods;

        const updateItem = createUpdateFunction(rowId);
        const course = _.find(courses, { id: rowId }) || {};
        const value = _.get(newValue, "value");
        const allStudents = _.get(newValue, "allStudents", []);
        const commonParams = { updateItem, value, allStudents };

        switch (colId) {
          case STUDENTS: // Students dropdown
            handleStudentChange({ ...commonParams, course });
            break;
          case OPENS_ON: // Opens on date
            handleOpenDateChange({ ...commonParams, course });
            break;
          case DUE_ON: // Due on date
            handleDueDateChange({ ...commonParams, course });
            break;
          case CLOSES_ON: // Closes on date
            handleCloseDateChange({ ...commonParams });
            break;
          case VISIBILITY: // Visibility dropdown
            handleVisibilityChange({ ...commonParams, course });
            break;
          case TAGS: // Tags
            handleTagChange({ ...commonParams, course });
            break;
          case SECURITY_MODE: // Security mode
            handleSecurityModeChange({ ...commonParams });
            break;
          case DURATION: // Duration
            handleDurationChange({ ...commonParams });
            break;
          default:
            break;
        }
      });
    },
    [
      courses,
      createUpdateFunction,
      handleStudentChange,
      handleOpenDateChange,
      handleDueDateChange,
      handleCloseDateChange,
      handleTagChange,
      handleVisibilityChange,
      handleSecurityModeChange,
      handleDurationChange,
    ]
  );

  // Define DataGrid headers
  const headers = useMemo(() => {
    return createDataGridHeaders({
      isFixedStartTime,
      classWiseSettingsToDisplay,
      t,
    });
  }, [isFixedStartTime, classWiseSettingsToDisplay, t]);

  // Transform courses data for DataGrid format
  const gridData = useMemo(() => {
    return transformCoursesToGridData({
      courses,
      coursesUserMap,
      visibilityOptionPreference,
      genericFolders,
      academicYearSelected,
      allowBackDate,
      selectedTimeSlot,
      isDraftMode,
      currentTimelineSettings,
      dueTimePreference,
      assignmentData,
      mode,
      isSubmissionPendingForAll,
      isStudentSubmissionEnabled,
      filteredStaffCoursesIds,
      isFixedStartTime,
      courseSelectedFoldersMap,
      courseId,
      isDuplicateAssignment,
      isIncludedInCourseFlow,
      handleDeleteIconClick: handleRemoveClass,
      assignmentsState,
      classWiseSettingsToDisplay,
      opensOnPreference,
      dueOnPreference,
      closesOnPreference,
      securityMode,
      timedTestDuration,
      t,
    });
  }, [
    courses,
    coursesUserMap,
    visibilityOptionPreference,
    genericFolders,
    academicYearSelected,
    allowBackDate,
    selectedTimeSlot,
    isDraftMode,
    currentTimelineSettings,
    dueTimePreference,
    assignmentData,
    mode,
    isSubmissionPendingForAll,
    isStudentSubmissionEnabled,
    filteredStaffCoursesIds,
    isFixedStartTime,
    courseSelectedFoldersMap,
    courseId,
    isDuplicateAssignment,
    isIncludedInCourseFlow,
    handleRemoveClass,
    assignmentsState,
    classWiseSettingsToDisplay,
    opensOnPreference,
    dueOnPreference,
    closesOnPreference,
    securityMode,
    timedTestDuration,
    t,
  ]);

  // Show empty state if no courses
  if (_.isEmpty(courses)) {
    return (
      <div className={classes.emptyStateContainer}>
        <EmptyState
          dsVersion="2.0"
          illustration={NoClassesIllustration}
          title={t("classRoom:no_classes_selected")}
          subtitle={t("classRoom:select_classes_to_assign_assignment")}
          primaryButton={
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="fill"
              icon={<AddCircleOutlined />}
              onClick={handleAddClassClick}
              testId={"assignmentPostSettings-addClass"}
            >
              {t("common:add_class")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={classes.datagridContainer}>
      <DataGrid
        data={gridData}
        headers={headers}
        onCellEdit={handleCellEdit}
        dataGridHeight="max-content"
        rowHeight={44}
        headerHeight={44}
        rowMarkers="none"
        columnSelectionEnabled={false}
        tooltipDelayTime={100}
        blurSelectionOnOutsideClick={true}
        roundedCorners={false}
        columnsResizable={false}
      />
      {isWorksheetLayoutUpdateEnabled ? (
        <WorksheetAssignSummary
          courses={courses}
          isFixedStartTime={isFixedStartTime}
          isTimedTestEnabled={isTimedTestEnabled}
          currentTimelineSettings={currentTimelineSettings}
          assignmentData={assignmentData}
          timedTestDuration={timedTestDuration}
        />
      ) : (
        <AssignClassSettingsDataGridFooter
          courses={courses}
          isFixedStartTime={isFixedStartTime}
          currentTimelineSettings={currentTimelineSettings}
          assignmentData={assignmentData}
          isDraftMode={isDraftMode}
          timedTestDuration={timedTestDuration}
        />
      )}
      {!_.isEmpty(removeClassDialogParams) ? (
        <Dialog
          title={t("common:remove_subject_label", {
            label: removedClassLabel,
          })}
          message={t("classRoom:unassign_class_dialog_message", {
            class: removedClassLabel,
            assessment: assessmentTitle,
          })}
          buttonTwoText={t("common:remove")}
          onClose={handleDialogClose}
          onButtonOneClick={handleDialogClose}
          onButtonTwoClick={() =>
            handleRemoveClassConfirm(
              _.get(removeClassDialogParams, "course.id")
            )
          }
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const visibilitySetting = getSettingValue({
    name: "AssignmentVisibilityOptionPreference",
    readFromUserAndOrgLevel: true,
  });
  const { isFixedStartTime } = ownProps;

  return {
    academicYearSelected: getSelectedAcademicYear(state),
    visibilityOptionPreference: _.get(visibilitySetting, "visibility", VISIBLE),
    allowBackDate: isFixedStartTime
      ? false
      : getSettingValue({
          name: "Common:BackDateAssignment",
          currentCurriculumProgramId: getSelectedCurriculumId(state),
        }),
  };
};

const mapActionCreators = {
  updateSettings,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(AssignClassSettingsDataGrid);
